import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartRefreshEvent } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const GIFT_CARD_HANDLE = 'gift-card';

/**
 * The customer-visible item count: line quantities minus the hidden TK
 * service lines (delivery fee, pickup marker). Same rule
 * as snippets/tk-visible-item-count.liquid, so the bubble and the Liquid
 * counts can never disagree.
 *
 * @param {{handle?: string, quantity?: number}[]} items - Ajax cart items.
 * @returns {number}
 */
function visibleItemCount(items) {
  const serviceHandles = ['tk-delivery-fee', 'tk-pickup-marker'];

  // TK: a freshly sliced line is one item, whatever its weight (8 × 1/4 lb is
  // 2 lb, not 8) — same rule as snippets/cart-bubble.liquid. So is a gift card:
  // amounts over the exact variants ride the $1 "Custom amount" variant with
  // quantity = dollars.
  const sliced = (item) => /freshly sliced/i.test(item.variant_title || '');
  return items.reduce((count, item) => {
    if (serviceHandles.includes(item.handle)) return count;
    if (sliced(item) || item.handle === GIFT_CARD_HANDLE) return count + 1;
    return count + item.quantity;
  }, 0);
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
      this.#reconcileWithServerCart();
    }
  };

  /**
   * bfcache hands the page back exactly as it was left — including a cart
   * painted before the shopper went to checkout. Nothing in the restored
   * document knows an order has since emptied it, and
   * `ensureCartBubbleIsCorrect` cannot tell either: it compares the painted
   * number against sessionStorage, and after an order both hold the same
   * pre-order count. The server is the only thing that knows, so ask it,
   * and announce what came back for the drawer and the cart page to act on.
   */
  #reconcileWithServerCart = async () => {
    let cart;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) return;
      cart = await response.json();
    } catch (error) {
      // Offline, or the shopper navigated straight back out. Leave the
      // painted state alone rather than guessing at it.
      return;
    }

    const items = Array.isArray(cart?.items) ? cart.items : null;
    const itemCount = items ? visibleItemCount(items) : (cart?.item_count ?? 0);

    if (itemCount === this.currentCartCount) return;

    // No animation: this is a correction to a number that was already wrong
    // on screen, not a cart action the shopper just took.
    this.renderCartBubble(itemCount, false);
    document.dispatchEvent(new CartRefreshEvent(itemCount));
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    // Optimistic paint for adds: the resolved count only arrives after two
    // network round trips (/cart/add then the cart refetch), so the bubble
    // sat on the old number for the whole exchange. The added quantity is
    // in the event payload, so current + added paints on the same frame as
    // the tap; the resolution below repaints only if the guess was wrong
    // (failed add, quantity capped by stock).
    const { action, lines } = /** @type {any} */ (event);
    if (action === 'add' && Array.isArray(lines) && Number.isFinite(this.currentCartCount)) {
      // A freshly sliced add stacks onto its line as weight, not items, so it
      // isn't guessed here; the resolved count below paints it. A gift card
      // add can carry its dollar amount as the quantity (the $1 "Custom
      // amount" variant); assets/tk-gift-card.js marks that product form, so
      // it counts one per line.
      const giftAdd = event.target instanceof Element && event.target.matches('[data-tk-gift-form]');
      const added = giftAdd
        ? lines.length
        : lines.reduce((sum, line) => sum + (line?.tkSliced ? 0 : Number(line?.quantity) || 0), 0);
      if (added > 0) this.renderCartBubble(this.currentCartCount + added);
    }

    event.promise
      ?.then(({ cart, detail }) => {
        // The standardized cart on the event has `lines` (no product
        // handles), never `items` — so the real AJAX items ride on
        // detail.items (every dispatcher passes them). Count those,
        // excluding the hidden TK service lines; detail.itemCount is the
        // Liquid-corrected section count, and raw totalQuantity (which
        // includes service lines) is the last resort.
        const items = Array.isArray(cart?.items) ? cart.items : Array.isArray(detail?.items) ? detail.items : null;
        const itemCount = items ? visibleItemCount(items) : (detail?.itemCount ?? cart?.totalQuantity ?? 0);

        // Repaint only when the number on screen is wrong — a same-value
        // repaint would replay the bubble animation for nothing.
        if (itemCount !== this.currentCartCount) this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
